/**
 * Social Links — Shared types and platform definitions
 * Used by web settings, mobile settings, and user profile modals
 */

export interface SocialLinks {
  twitter?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  discord?: string;
  twitch?: string;
  actionNetwork?: string;
}

export const SOCIAL_PLATFORMS = [
  { key: 'twitter', label: 'Twitter / X', icon: 'Twitter', urlPrefix: 'https://x.com/' },
  { key: 'instagram', label: 'Instagram', icon: 'Instagram', urlPrefix: 'https://instagram.com/' },
  { key: 'tiktok', label: 'TikTok', icon: 'Music2', urlPrefix: 'https://tiktok.com/@' },
  { key: 'youtube', label: 'YouTube', icon: 'Youtube', urlPrefix: 'https://youtube.com/@' },
  { key: 'discord', label: 'Discord', icon: 'MessageCircle', urlPrefix: '' },
  { key: 'twitch', label: 'Twitch', icon: 'Twitch', urlPrefix: 'https://twitch.tv/' },
  {
    key: 'actionNetwork',
    label: 'Action Network',
    icon: 'Target',
    urlPrefix: 'https://actionnetwork.com/',
  },
] as const;

export type SocialPlatformKey = keyof SocialLinks;

/**
 * Strip common prefixes from social handles.
 * Accepts full URLs or @-prefixed usernames and returns just the username.
 */
export function cleanSocialHandle(platform: SocialPlatformKey, input: string): string {
  let value = input.trim();

  // Strip leading @
  if (value.startsWith('@')) {
    value = value.slice(1);
  }

  // Strip full URL prefixes
  const platformConfig = SOCIAL_PLATFORMS.find((p) => p.key === platform);
  if (platformConfig?.urlPrefix && value.startsWith(platformConfig.urlPrefix)) {
    value = value.slice(platformConfig.urlPrefix.length);
  }

  // Also strip common URL patterns
  const urlPatterns = [
    /^https?:\/\/(www\.)?(twitter|x)\.com\//i,
    /^https?:\/\/(www\.)?instagram\.com\//i,
    /^https?:\/\/(www\.)?tiktok\.com\/@?/i,
    /^https?:\/\/(www\.)?youtube\.com\/@?/i,
    /^https?:\/\/(www\.)?twitch\.tv\//i,
    /^https?:\/\/(www\.)?actionnetwork\.com\//i,
  ];

  for (const pattern of urlPatterns) {
    value = value.replace(pattern, '');
  }

  // Strip trailing slashes
  value = value.replace(/\/+$/, '');

  return value;
}

/**
 * Build the full URL for a social platform given a handle.
 * Returns empty string for platforms without URL prefix (e.g., Discord).
 */
export function buildSocialUrl(platform: SocialPlatformKey, handle: string): string {
  const config = SOCIAL_PLATFORMS.find((p) => p.key === platform);
  if (!config?.urlPrefix || !handle) return '';
  return `${config.urlPrefix}${handle}`;
}
